"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Button, Text } from "@zapier/design-system";
import { useCopyToClipboard } from "@zapier/shared";

import styles from "./DebugAiNotesPanel.module.css";

export type DebugAiNotesPanelStatus = "generating" | "success" | "error";

const COPY_NOTIFICATION_MESSAGE = "Copied!";
const DEBUG_PANEL_TITLE = "Debug: AI requests";
const DEBUG_EXPAND_ALL_LABEL = "Expand all";
const DEBUG_COLLAPSE_ALL_LABEL = "Collapse all";
const DEBUG_COPY_LABEL = "Copy";
const DEBUG_NO_PAYLOAD_TEXT = "No debug payload was returned.";
const DEBUG_STATUS_LABEL = "Status";
const DEBUG_SYSTEM_MESSAGE_LABEL = "System message";
const DEBUG_USER_PAYLOAD_LABEL = "User payload";
const DEBUG_UNEXPECTED_PAYLOAD_TEXT =
  "Debug payload had an unexpected shape, so we can’t safely show what was sent to AI.";
const DEBUG_RESULT_MODE_LABEL = "Result mode:";
const DEBUG_NOTES_CALL_LABEL = "Notes AI call:";
const DEBUG_ZAP_SUMMARIES_CALL_LABEL = "Zap summaries AI call:";
const DEBUG_UNKNOWN = "(unknown)";
const DEBUG_SENT_ZAP_SUMMARIES_LABEL = "SENT: Zap summaries";
const DEBUG_SENT_FOLDER_NOTES_LABEL = "SENT: Folder notes";
const DEBUG_NOT_SENT_FOLDER_NOTES_TEXT =
  "NOT SENT: Folder notes (generated deterministically)";
const DEBUG_SENT_CONFIRMED_TEXT = "sent (confirmed)";
const DEBUG_NOT_SENT_NOTES_TEXT = "not sent (deterministic or no-call)";
const DEBUG_NOT_SENT_ZAP_SUMMARIES_TEXT = "not sent";
const DEBUG_UNKNOWN_CALL_TEXT = "unknown";
const DEBUG_NO_CONFIRMED_SENT_GENERATING_TEXT =
  "Generating… waiting for AI request payloads to be confirmed sent.";
const DEBUG_NO_CONFIRMED_SENT_SUCCESS_TEXT =
  "No AI request payloads were confirmed sent for this run.";
const DEBUG_NO_CONFIRMED_SENT_ERROR_TEXT =
  "No AI request payloads were confirmed sent (generation failed before sending).";
const DEBUG_PREFLIGHT_ERROR_LABEL = "Preflight error";
const DEBUG_ZSL_RESPONSE_LABEL = "ZSL-API fetch response";
const DEBUG_ZSL_SUCCEEDED_LABEL = "Succeeded";
const DEBUG_ZSL_DURATION_LABEL = "Duration (ms)";
const DEBUG_ZSL_ERROR_MESSAGE_LABEL = "Error message";
const DEBUG_ZSL_RESPONSE_BODY_LABEL = "Response";

interface PromptPayload {
  systemMessage: string;
  promptPayload: unknown;
}

interface DebugPayloadShape {
  preflight?: unknown;
  preflightError?: string;
  result?: {
    mode?: string;
    folderNotesLlmCalled?: boolean;
    zapSummariesLlmCalled?: boolean;
    folderNotes?: PromptPayload;
    zapSummaries?: PromptPayload;
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function getString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function getPromptPayload(value: unknown): PromptPayload | undefined {
  if (!isObject(value)) return;
  const systemMessage = getString(value.systemMessage);
  if (!systemMessage) return;
  if (!("promptPayload" in value)) return;
  return { systemMessage, promptPayload: (value as any).promptPayload };
}

function getDebugResult(
  value: unknown,
): DebugPayloadShape["result"] | undefined {
  if (!isObject(value)) return;
  const result = (value as any).result;
  return isObject(result) ? (result as any) : undefined;
}

function getDebugPreflight(value: unknown): DebugPayloadShape["preflight"] {
  if (!isObject(value)) return;
  const preflight = (value as any).preflight;
  return isObject(preflight) ? (preflight as any) : undefined;
}

function getPreflightError(value: unknown): string | undefined {
  if (!isObject(value)) return;
  const error = (value as any).preflightError;
  return typeof error === "string" && error.trim().length > 0
    ? error.trim()
    : undefined;
}

interface ZslGenerateSection {
  succeeded?: boolean;
  durationMs?: number;
  errorMessage?: string;
  value?: unknown;
}

function getZslFetchSection(
  value: unknown,
  key: "zslFetchRaw",
): ZslGenerateSection | undefined {
  if (!isObject(value)) return;
  const section = (value as any)[key];
  return isObject(section) ? (section as any) : undefined;
}

function stringifyUserMessage(promptPayload: unknown): string {
  return JSON.stringify(promptPayload, undefined, 2);
}

function formatSentMessagesReadable(
  label: string,
  payload: PromptPayload,
): string {
  return [
    `${label}:`,
    "",
    "SYSTEM MESSAGE:",
    payload.systemMessage,
    "",
    "USER PAYLOAD:",
    stringifyUserMessage(payload.promptPayload),
  ].join("\n");
}

function PrettyJson({ value }: { value: unknown }) {
  const text = useMemo(() => JSON.stringify(value, undefined, 2), [value]);
  return <pre className={styles.codeBlock}>{text}</pre>;
}

function PromptPayloadDetails({
  label,
  payload,
  copyText,
  onCopy,
  isOpen,
  onToggle,
}: {
  label: string;
  payload: PromptPayload;
  copyText?: string;
  onCopy: (text: string) => void;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}) {
  return (
    <Accordion
      title={label}
      isOpen={isOpen}
      onToggle={onToggle}
      toggleSide="left"
    >
      <div className={styles.payloadHeaderRow}>
        <Text tag="p" type="Body3">
          {DEBUG_SYSTEM_MESSAGE_LABEL}
        </Text>
        <Button
          type="button"
          variant="tertiary"
          size="small"
          onPress={() => {
            onCopy(payload.systemMessage);
          }}
        >
          {DEBUG_COPY_LABEL}
        </Button>
      </div>
      <pre className={`${styles.codeBlock} ${styles.codeBlockSoftWrap}`}>
        {payload.systemMessage}
      </pre>
      <div
        className={`${styles.payloadHeaderRow} ${styles.payloadHeaderRowAfterSystemMessage}`}
      >
        <Text tag="p" type="Body3">
          {DEBUG_USER_PAYLOAD_LABEL}
        </Text>
        <Button
          type="button"
          variant="tertiary"
          size="small"
          onPress={() => {
            if (!copyText) return;
            onCopy(copyText);
          }}
          isDisabled={!copyText}
        >
          {DEBUG_COPY_LABEL}
        </Button>
      </div>
      <PrettyJson value={payload.promptPayload} />
    </Accordion>
  );
}

function ZslGenerateRawDetails({
  payload,
  isOpen,
  onToggle,
  title,
  copyText,
  onCopy,
}: {
  payload: {
    succeeded?: boolean;
    durationMs?: number;
    errorMessage?: string;
    value?: unknown;
  };
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
  title: string;
  copyText?: string;
  onCopy: (text: string) => void;
}) {
  const succeededText =
    typeof payload.succeeded === "boolean"
      ? String(payload.succeeded)
      : DEBUG_UNKNOWN;
  const durationText =
    typeof payload.durationMs === "number"
      ? String(payload.durationMs)
      : DEBUG_UNKNOWN;
  const errorText =
    typeof payload.errorMessage === "string" &&
    payload.errorMessage.trim().length > 0
      ? payload.errorMessage.trim()
      : undefined;

  return (
    <Accordion
      title={title}
      isOpen={isOpen}
      onToggle={onToggle}
      toggleSide="left"
    >
      <Text tag="p" type="Body3">
        {DEBUG_ZSL_SUCCEEDED_LABEL}: {succeededText}
      </Text>
      <Text tag="p" type="Body3">
        {DEBUG_ZSL_DURATION_LABEL}: {durationText}
      </Text>
      {errorText ? (
        <>
          <Text tag="p" type="Body3">
            {DEBUG_ZSL_ERROR_MESSAGE_LABEL}
          </Text>
          <pre className={styles.codeBlock}>{errorText}</pre>
        </>
      ) : undefined}
      {"value" in payload ? (
        <>
          <div className={styles.payloadHeaderRow}>
            <Text tag="p" type="Body3">
              {DEBUG_ZSL_RESPONSE_BODY_LABEL}
            </Text>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              onPress={() => {
                if (!copyText) return;
                onCopy(copyText);
              }}
              isDisabled={!copyText}
            >
              {DEBUG_COPY_LABEL}
            </Button>
          </div>
          <PrettyJson value={(payload as any).value} />
        </>
      ) : undefined}
    </Accordion>
  );
}

interface Props {
  status: DebugAiNotesPanelStatus;
  errorMessage?: string;
  debugPayload?: unknown;
}

export function DebugAiNotesPanel({
  status,
  errorMessage,
  debugPayload,
}: Props) {
  const copyToClipboard = useCopyToClipboard();
  const debugResult = useMemo(
    () => getDebugResult(debugPayload),
    [debugPayload],
  );
  const debugPreflight = useMemo(
    () => getDebugPreflight(debugPayload),
    [debugPayload],
  );
  const preflightError = useMemo(
    () => getPreflightError(debugPayload),
    [debugPayload],
  );
  const zslFetchRaw = useMemo(
    () => getZslFetchSection(debugPreflight, "zslFetchRaw"),
    [debugPreflight],
  );
  const [isZapSummariesOpen, setIsZapSummariesOpen] = useState(true);
  const [isFolderNotesOpen, setIsFolderNotesOpen] = useState(true);
  const [isZslFetchOpen, setIsZslFetchOpen] = useState(true);

  const notesCalledFlag = getBoolean(debugResult?.folderNotesLlmCalled);
  const zapSummariesCalledFlag = getBoolean(debugResult?.zapSummariesLlmCalled);
  const zapSummariesPayload = getPromptPayload(debugResult?.zapSummaries);
  const folderNotesPayload = getPromptPayload(debugResult?.folderNotes);
  const zapSummariesCopyText = useMemo(() => {
    if (!zapSummariesPayload) return;
    return formatSentMessagesReadable(
      DEBUG_SENT_ZAP_SUMMARIES_LABEL,
      zapSummariesPayload,
    );
  }, [zapSummariesPayload]);
  const folderNotesCopyText = useMemo(() => {
    if (!folderNotesPayload) return;
    return formatSentMessagesReadable(
      DEBUG_SENT_FOLDER_NOTES_LABEL,
      folderNotesPayload,
    );
  }, [folderNotesPayload]);

  const formatZslCopyText = useCallback((label: string, p: any) => {
    const succeeded =
      typeof p?.succeeded === "boolean" ? String(p.succeeded) : DEBUG_UNKNOWN;
    const duration =
      typeof p?.durationMs === "number" ? String(p.durationMs) : DEBUG_UNKNOWN;
    const error =
      typeof p?.errorMessage === "string" && p.errorMessage.trim().length > 0
        ? p.errorMessage.trim()
        : undefined;
    const value =
      "value" in (p ?? {})
        ? JSON.stringify(p.value, undefined, 2)
        : DEBUG_UNKNOWN;

    return [
      `${label}:`,
      "",
      `${DEBUG_ZSL_SUCCEEDED_LABEL}: ${succeeded}`,
      `${DEBUG_ZSL_DURATION_LABEL}: ${duration}`,
      ...(error ? ["", `${DEBUG_ZSL_ERROR_MESSAGE_LABEL}:`, error] : []),
      "",
      `${DEBUG_ZSL_RESPONSE_BODY_LABEL}:`,
      value,
    ].join("\n");
  }, []);

  const notesWasSent =
    notesCalledFlag === true ||
    (!!folderNotesPayload && notesCalledFlag === undefined);
  const zapSummariesWasSent =
    zapSummariesCalledFlag === true ||
    (!!zapSummariesPayload && zapSummariesCalledFlag === undefined);

  const notesCallText = notesWasSent
    ? DEBUG_SENT_CONFIRMED_TEXT
    : notesCalledFlag === false
      ? DEBUG_NOT_SENT_NOTES_TEXT
      : DEBUG_UNKNOWN_CALL_TEXT;

  const zapSummariesCallText = zapSummariesWasSent
    ? DEBUG_SENT_CONFIRMED_TEXT
    : zapSummariesCalledFlag === false
      ? DEBUG_NOT_SENT_ZAP_SUMMARIES_TEXT
      : DEBUG_UNKNOWN_CALL_TEXT;

  const hasZapSummaries = !!zapSummariesPayload && zapSummariesWasSent;
  const hasFolderNotes = !!folderNotesPayload && notesWasSent;
  const hasZslFetch = !!zslFetchRaw;
  const hasAnyAccordionSection =
    hasZapSummaries || hasFolderNotes || hasZslFetch;

  const allExpanded =
    (!hasZapSummaries || isZapSummariesOpen) &&
    (!hasFolderNotes || isFolderNotesOpen) &&
    (!hasZslFetch || isZslFetchOpen);
  const expandCollapseAllLabel = allExpanded
    ? DEBUG_COLLAPSE_ALL_LABEL
    : DEBUG_EXPAND_ALL_LABEL;

  const zslFetchHasValue = !!(zslFetchRaw && "value" in zslFetchRaw);
  const zslFetchSucceeded = zslFetchRaw?.succeeded;
  const zslFetchDurationMs = zslFetchRaw?.durationMs;

  const autoExpandKey = useMemo(() => {
    if (debugPayload === undefined) return "no-debug-payload";

    const mode = getString(debugResult?.mode) ?? "";
    const notesCalled =
      notesCalledFlag === undefined ? "u" : notesCalledFlag ? "1" : "0";
    const zapCalled =
      zapSummariesCalledFlag === undefined
        ? "u"
        : zapSummariesCalledFlag
          ? "1"
          : "0";

    const fetch = hasZslFetch
      ? `s:${zslFetchSucceeded === undefined ? "u" : zslFetchSucceeded ? "1" : "0"},d:${zslFetchDurationMs ?? ""},v:${zslFetchHasValue ? "1" : "0"}`
      : "";

    const preError = preflightError ? "1" : "0";
    const zapSysLength = zapSummariesPayload?.systemMessage.length ?? "";
    const notesSysLength = folderNotesPayload?.systemMessage.length ?? "";

    return [
      `m:${mode}`,
      `n:${notesCalled}`,
      `z:${zapCalled}`,
      `p:${preError}`,
      `fetch:${fetch}`,
      `zapSys:${zapSysLength}`,
      `notesSys:${notesSysLength}`,
    ].join("|");
  }, [
    debugPayload,
    debugResult?.mode,
    folderNotesPayload?.systemMessage.length,
    hasZslFetch,
    notesCalledFlag,
    preflightError,
    zapSummariesCalledFlag,
    zapSummariesPayload?.systemMessage.length,
    zslFetchDurationMs,
    zslFetchSucceeded,
    zslFetchHasValue,
  ]);

  const lastAutoExpandedKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Auto-expand once per distinct payload. This prevents manual toggles
    // (collapse-all or individual accordion toggles) from being immediately
    // overridden by payload re-renders.
    if (autoExpandKey === lastAutoExpandedKey.current) return;
    lastAutoExpandedKey.current = autoExpandKey;

    setIsZapSummariesOpen(!!(zapSummariesPayload && zapSummariesWasSent));
    setIsFolderNotesOpen(!!(folderNotesPayload && notesWasSent));
    setIsZslFetchOpen(!!zslFetchRaw);
  }, [
    autoExpandKey,
    folderNotesPayload,
    notesWasSent,
    zapSummariesPayload,
    zapSummariesWasSent,
    zslFetchRaw,
  ]);

  const debugTextForCopy = useMemo(() => {
    if (debugPayload === undefined) return;

    const result = getDebugResult(debugPayload);
    if (!result) return;

    const lines: string[] = [];

    const notesCalledFlag = getBoolean(result.folderNotesLlmCalled);
    const zapSummariesCalledFlag = getBoolean(result.zapSummariesLlmCalled);
    const zapSummariesPayload = getPromptPayload(result.zapSummaries);
    const folderNotesPayload = getPromptPayload(result.folderNotes);

    const notesWasSent =
      notesCalledFlag === true ||
      (!!folderNotesPayload && notesCalledFlag === undefined);
    const zapSummariesWasSent =
      zapSummariesCalledFlag === true ||
      (!!zapSummariesPayload && zapSummariesCalledFlag === undefined);

    const notesCallText = notesWasSent
      ? DEBUG_SENT_CONFIRMED_TEXT
      : notesCalledFlag === false
        ? DEBUG_NOT_SENT_NOTES_TEXT
        : DEBUG_UNKNOWN_CALL_TEXT;
    const zapSummariesCallText = zapSummariesWasSent
      ? DEBUG_SENT_CONFIRMED_TEXT
      : zapSummariesCalledFlag === false
        ? DEBUG_NOT_SENT_ZAP_SUMMARIES_TEXT
        : DEBUG_UNKNOWN_CALL_TEXT;

    const mode = getString(result.mode);
    if (mode) {
      lines.push(`Result mode: ${mode}`);
    }
    lines.push(
      `Notes AI call: ${notesCallText}`,
      `Zap summaries AI call: ${zapSummariesCallText}`,
      "",
    );

    if (zapSummariesPayload && zapSummariesWasSent) {
      lines.push(
        formatSentMessagesReadable(
          DEBUG_SENT_ZAP_SUMMARIES_LABEL,
          zapSummariesPayload,
        ),
        "",
      );
    }

    if (folderNotesPayload && notesWasSent) {
      lines.push(
        formatSentMessagesReadable(
          DEBUG_SENT_FOLDER_NOTES_LABEL,
          folderNotesPayload,
        ),
        "",
      );
    } else if (notesCalledFlag === false) {
      lines.push(
        "NOT SENT: Folder notes",
        "Reason: generated deterministically (no notes LLM call).",
        "",
      );
    }

    return lines.join("\n");
  }, [debugPayload]);

  const statusText = useMemo(() => {
    if (status === "generating") return `${DEBUG_STATUS_LABEL}: generating`;
    if (status === "success") return `${DEBUG_STATUS_LABEL}: success`;
    if (errorMessage) return `${DEBUG_STATUS_LABEL}: error — ${errorMessage}`;
    return `${DEBUG_STATUS_LABEL}: error`;
  }, [errorMessage, status]);

  const noConfirmedSentText = useMemo(() => {
    if (status === "generating") return DEBUG_NO_CONFIRMED_SENT_GENERATING_TEXT;
    if (status === "success") return DEBUG_NO_CONFIRMED_SENT_SUCCESS_TEXT;
    return DEBUG_NO_CONFIRMED_SENT_ERROR_TEXT;
  }, [status]);

  const handleToggleExpandCollapseAll = useCallback(() => {
    if (!hasAnyAccordionSection) return;
    const nextOpen = !allExpanded;
    if (hasZapSummaries) setIsZapSummariesOpen(nextOpen);
    if (hasFolderNotes) setIsFolderNotesOpen(nextOpen);
    if (hasZslFetch) setIsZslFetchOpen(nextOpen);
  }, [
    allExpanded,
    hasAnyAccordionSection,
    hasFolderNotes,
    hasZapSummaries,
    hasZslFetch,
  ]);

  const handleCopy = useCallback(() => {
    if (!debugTextForCopy) return;
    void copyToClipboard(debugTextForCopy, {
      notificationMessage: COPY_NOTIFICATION_MESSAGE,
    });
  }, [copyToClipboard, debugTextForCopy]);

  const handleCopySection = useCallback(
    (text: string) => {
      void copyToClipboard(text, {
        notificationMessage: COPY_NOTIFICATION_MESSAGE,
      });
    },
    [copyToClipboard],
  );

  return (
    <div className={styles.debugPanel}>
      <div className={styles.debugHeader}>
        <Text tag="h3" type="SectionHeader">
          {DEBUG_PANEL_TITLE}
        </Text>
        <div className={styles.debugHeaderActions}>
          {debugResult && hasAnyAccordionSection ? (
            <Button
              type="button"
              variant="tertiary"
              size="small"
              onPress={handleToggleExpandCollapseAll}
            >
              {expandCollapseAllLabel}
            </Button>
          ) : undefined}
          <Button
            type="button"
            variant="tertiary"
            size="small"
            onPress={handleCopy}
            isDisabled={!debugTextForCopy}
          >
            {DEBUG_COPY_LABEL}
          </Button>
        </div>
      </div>
      <div className={styles.debugBody}>
        <div className={styles.debugSummary}>
          <Text tag="p" type="Body3">
            {statusText}
          </Text>
        </div>

        {debugPayload === undefined ? (
          <Text tag="p" type="Body3">
            {DEBUG_NO_PAYLOAD_TEXT}
          </Text>
        ) : debugResult ? (
          <>
            <div className={styles.debugSection}>
              <Text tag="p" type="Body3">
                {DEBUG_RESULT_MODE_LABEL}{" "}
                {getString(debugResult.mode) ?? DEBUG_UNKNOWN}
              </Text>
              <Text tag="p" type="Body3">
                {DEBUG_NOTES_CALL_LABEL} {notesCallText}
              </Text>
              <Text tag="p" type="Body3">
                {DEBUG_ZAP_SUMMARIES_CALL_LABEL} {zapSummariesCallText}
              </Text>
            </div>

            {preflightError ? (
              <div className={styles.debugSection}>
                <Text tag="p" type="Body3">
                  {DEBUG_PREFLIGHT_ERROR_LABEL}
                </Text>
                <pre className={styles.codeBlock}>{preflightError}</pre>
              </div>
            ) : undefined}

            {zslFetchRaw ? (
              <ZslGenerateRawDetails
                payload={zslFetchRaw}
                isOpen={isZslFetchOpen}
                onToggle={(isOpen) => setIsZslFetchOpen(isOpen)}
                title={DEBUG_ZSL_RESPONSE_LABEL}
                copyText={formatZslCopyText(
                  DEBUG_ZSL_RESPONSE_LABEL,
                  zslFetchRaw,
                )}
                onCopy={handleCopySection}
              />
            ) : undefined}

            {zapSummariesPayload && zapSummariesWasSent ? (
              <PromptPayloadDetails
                label={DEBUG_SENT_ZAP_SUMMARIES_LABEL}
                payload={zapSummariesPayload}
                copyText={zapSummariesCopyText}
                onCopy={handleCopySection}
                isOpen={isZapSummariesOpen}
                onToggle={(isOpen) => setIsZapSummariesOpen(isOpen)}
              />
            ) : undefined}

            {folderNotesPayload && notesWasSent ? (
              <PromptPayloadDetails
                label={DEBUG_SENT_FOLDER_NOTES_LABEL}
                payload={folderNotesPayload}
                copyText={folderNotesCopyText}
                onCopy={handleCopySection}
                isOpen={isFolderNotesOpen}
                onToggle={(isOpen) => setIsFolderNotesOpen(isOpen)}
              />
            ) : notesCalledFlag === false ? (
              <Text tag="p" type="Body3">
                {DEBUG_NOT_SENT_FOLDER_NOTES_TEXT}
              </Text>
            ) : undefined}
          </>
        ) : isObject(debugPayload) ? (
          <Text tag="p" type="Body3">
            {noConfirmedSentText}
          </Text>
        ) : (
          <Text tag="p" type="Body3">
            {DEBUG_UNEXPECTED_PAYLOAD_TEXT}
          </Text>
        )}
      </div>
    </div>
  );
}
